<script setup lang="ts">
import { useCommerceStore } from '~/stores/pos/commerce'

const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const commerceStore = useCommerceStore()
const { cartTotal, cashReceived, calculatedChange } = storeToRefs(commerceStore)
const { setCashReceived, resetCashCalculation } = commerceStore

// Local ref for NumberPad binding
const localCashReceived = ref(0)
const displayValue = ref('0')
const dialogContent = ref<HTMLElement | null>(null)

const changeColor = computed(() => {
  if (!cashReceived.value) return 'text-gray-600'
  return cashReceived.value >= Number(cartTotal.value) ? 'text-emerald-600' : 'text-red-600'
})

// Reset cash received when dialog opens
watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    resetCashCalculation()
    localCashReceived.value = 0
    displayValue.value = '0'
  }
})

// Sync displayValue with localCashReceived (for keyboard input)
watch(displayValue, (newValue) => {
  const numValue = Number.parseFloat(newValue) || 0
  if (numValue !== localCashReceived.value) {
    localCashReceived.value = numValue
  }
})

// Update store when local value changes (from either keyboard or NumberPad)
watch(localCashReceived, (newValue) => {
  setCashReceived(newValue)
})

function handleKeyboardInput(event: KeyboardEvent) {
  // Only handle keyboard input when dialog is visible
  if (!props.visible) return

  // Handle number keys
  if (event.key >= '0' && event.key <= '9') {
    event.preventDefault()
    event.stopPropagation()
    handleDigit(event.key)
  }
  // Handle decimal point
  else if (event.key === '.' || event.key === ',') {
    event.preventDefault()
    event.stopPropagation()
    handleDecimal()
  }
  // Handle backspace
  else if (event.key === 'Backspace') {
    event.preventDefault()
    event.stopPropagation()
    handleBackspace()
  }
  // Handle clear (c or C)
  else if (event.key === 'c' || event.key === 'C') {
    event.preventDefault()
    event.stopPropagation()
    handleClear()
  }
  // Handle Enter or Escape to close
  else if (event.key === 'Enter' || event.key === 'Escape') {
    event.preventDefault()
    event.stopPropagation()
    closeDialog()
  }
}

function handleDigit(digit: string) {
  const decimalIndex = displayValue.value.indexOf('.')
  if (decimalIndex !== -1) {
    const decimalPlaces = displayValue.value.length - decimalIndex - 1
    if (decimalPlaces >= 2) {
      return
    }
  }

  if (displayValue.value === '0') {
    displayValue.value = digit
  }
  else {
    displayValue.value += digit
  }
}

function handleDecimal() {
  if (!displayValue.value.includes('.')) {
    displayValue.value += '.'
  }
}

function handleClear() {
  displayValue.value = '0'
}

function handleBackspace() {
  if (displayValue.value.length > 1) {
    displayValue.value = displayValue.value.slice(0, -1)
  }
  else {
    displayValue.value = '0'
  }
}

function closeDialog() {
  emit('update:visible', false)
}

// Add keyboard listener when dialog is visible
onMounted(() => {
  watch(() => props.visible, (isVisible) => {
    if (isVisible) {
      // Use capture phase to catch events before they reach other elements
      window.addEventListener('keydown', handleKeyboardInput, true)
      // Focus the dialog content to ensure it captures keyboard events
      nextTick(() => {
        dialogContent.value?.focus()
      })
    }
    else {
      window.removeEventListener('keydown', handleKeyboardInput, true)
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyboardInput, true)
})
</script>

<template>
  <AppDialog
    :visible="visible"
    modal
    :header="$t('Calculate Change')"
    :style="{ width: '90vw', maxWidth: '500px' }"
    @update:visible="emit('update:visible', $event)"
  >
    <div
      ref="dialogContent"
      tabindex="-1"
      class="flex flex-col gap-4 outline-none"
    >
      <!-- Total Display -->
      <div class="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
        <span class="text-base font-semibold text-gray-700">{{ $t('Total') }}:</span>
        <span class="text-xl font-bold text-gray-900">{{ usePriceFormatter(Number(cartTotal)) }}</span>
      </div>

      <!-- Cash Received Display -->
      <div class="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
        <span class="text-base font-semibold text-gray-700">{{ $t('Cash Received') }}:</span>
        <span class="text-xl font-bold text-blue-600">{{ usePriceFormatter(cashReceived || 0) }}</span>
      </div>

      <!-- Change Display -->
      <div class="flex justify-between items-center p-3 bg-emerald-50 rounded-lg border-2 border-emerald-200">
        <span class="text-base font-semibold text-gray-700">{{ $t('Change') }}:</span>
        <span
          class="text-2xl font-bold"
          :class="changeColor"
        >{{ usePriceFormatter(calculatedChange || 0) }}</span>
      </div>

      <!-- NumberPad Component -->
      <div class="mt-2">
        <NumberPad v-model="localCashReceived" />
      </div>

      <!-- Close Button -->
      <div class="flex justify-end mt-2">
        <Button
          :label="$t('Close')"
          severity="secondary"
          @click="closeDialog"
        />
      </div>
    </div>
  </AppDialog>
</template>
